import type { PackagingComponentInput } from '@/shared/hooks/api/packagingSuppliers'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ComponentEditorLabels } from './componentEditorTypes'
import {
  formatBoolean,
  getMaterialLabel,
  getRecyclingStreamLabel,
} from './componentEditorUtils'
import { formatComponentRecyclingPctDisplay } from './componentRecyclingPct'
import { formatComponentWeightGramsDisplay } from './componentWeightGrams'

type ComponentsTableProps = {
  components: PackagingComponentInput[]
  weightPercentages?: number[]
  isSaving?: boolean
  labels: ComponentEditorLabels
  onEdit: (index: number) => void
  onRemove: (index: number) => void
  showActions?: boolean
}

export function ComponentsTable({
  components,
  weightPercentages,
  isSaving = false,
  labels,
  onEdit,
  onRemove,
  showActions = true,
}: ComponentsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{labels.tableName}</TableHead>
          <TableHead>{labels.tableMaterial}</TableHead>
          <TableHead>{labels.tableWeight}</TableHead>
          <TableHead>{labels.tableWeightPercent}</TableHead>
          <TableHead>{labels.tableRecyclable}</TableHead>
          <TableHead>{labels.tableRecyclingStream}</TableHead>
          <TableHead>{labels.tableRecyclingPercent}</TableHead>
          <TableHead>{labels.tableReusable}</TableHead>
          {showActions && <TableHead>{labels.tableActions}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {components.map((component, index) => {
          const weightPercentage = weightPercentages?.[index]
          const hasWeightPercentage = weightPercentage !== undefined
          let weightPercentageLabel = '—'
          if (hasWeightPercentage) {
            weightPercentageLabel = `${weightPercentage.toFixed(1)}%`
          }

          return (
            <TableRow key={index}>
              <TableCell className='font-medium'>
                {component.name.trim() || '—'}
              </TableCell>
              <TableCell>
                {getMaterialLabel(component.material, labels)}
              </TableCell>
              <TableCell>
                {formatComponentWeightGramsDisplay(component.weightInGrams)}
              </TableCell>
              <TableCell>{weightPercentageLabel}</TableCell>
              <TableCell>
                {formatBoolean(component.recyclable, labels)}
              </TableCell>
              <TableCell>
                {getRecyclingStreamLabel(component.recyclingStream, labels)}
              </TableCell>
              <TableCell>
                {formatComponentRecyclingPctDisplay(component.recyclingPct)}
              </TableCell>
              <TableCell>{formatBoolean(component.reusable, labels)}</TableCell>
              {showActions && (
                <TableCell>
                  <div className='flex gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      disabled={isSaving}
                      onClick={() => {
                        onEdit(index)
                      }}
                    >
                      {labels.edit}
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      disabled={isSaving}
                      onClick={() => {
                        onRemove(index)
                      }}
                    >
                      {labels.remove}
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
